import { EmployeeListRecord, EmployeeRecord } from "../../../../../models/DSF/EmployeeDatabase";
import { RouteComponentProps } from "../../../../../models/RouteComponent";
import useApiService from "../../../../../hooks/useApiService";
import { Typography } from "@mui/joy";
import { useState } from "react";
import { TabPanel, TabView } from "primereact/tabview";
import { CompanyDocumentRecord } from "../../../../../models/DSF/CompanyDocuments";
import "./employee.scss";
import {
  CheckIcon,
  InfoCircleIcon,
  MinusIcon,
  CancelIcon,
  ProgressActivity,
  Download,
  DownloadOff,
} from "../../../../../assets/icons";
import { Accordion, AccordionTab } from "primereact/accordion";
import { formatDate } from "../../../../../utils/DateUtils";
import { downloadFile } from "../../../../../service/s3Service";

interface EmployeeProps extends RouteComponentProps {
  employeeId: string;
}

function Employee({ employeeId, ...props }: EmployeeProps) {
  const [activeIndex, setActiveIndex] = useState(0);
  const { session } = props;

  const [employee, setEmployee] = useState<EmployeeRecord | null>(null);
  const [documents, setDocuments] = useState<CompanyDocumentRecord[]>([]);
  const [downloadingArray, setDownloadingArray] = useState<string[]>([]);

  const { loading: employeeLoading, error: employeeError } = useApiService<EmployeeRecord>({
    method: "GET",
    url: `/employee-database/${employeeId}`,
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
    onSuccess: data => {
      setEmployee(data);
    },
  });

  const {
    loading: documentsLoading,
    error: documentsError,
    fetchData: fetchDocuments,
  } = useApiService<CompanyDocumentRecord[]>({
    method: "GET",
    url: `/employee-database/${employeeId}/documents`,
    apiName: "open",
    params: {
      clientId: session.client_id,
      siteId: session.site_id,
    },
    onSuccess: data => {
      setDocuments(data);
    },
    fetchOnMount: false,
  });

  const handleTabChange = async (e: any) => {
    setActiveIndex(e.index);
    if (!documents || (documents && documents.length === 0)) {
      fetchDocuments();
    }
  };

  const downloadS3File = async (doc: CompanyDocumentRecord) => {
    const downloading = [...downloadingArray, doc.id];
    setDownloadingArray(downloading);
    await downloadFile(doc.name, doc.s3_key);
    const filtered = downloading.filter(item => item !== doc.id);
    setDownloadingArray(filtered);
  };

  // console.log("data == ", data);

  const statusBodyTemplate = (value: string) => {
    switch (value) {
      case "success":
        return (
          <div className={"icon"}>
            <CheckIcon className={"success-check"} />
          </div>
        );
      case "warning":
        return (
          <div className={"icon"}>
            <InfoCircleIcon className={"warning-icon"} />
          </div>
        );
      case "none":
        return (
          <div className={"icon"}>
            <MinusIcon className={"minus-icon"} />
          </div>
        );
      case "fail":
      default:
        return (
          <div className={"icon"}>
            <CancelIcon className={"cancel-icon"} />
          </div>
        );
    }
  };

  return (
    <>
      <TabView onTabChange={handleTabChange} activeIndex={activeIndex}>
        <TabPanel header={"Employee details"} key={"employee-details"}>
          {employeeError ? (
            <div>
              <Typography level="h3" component="h3">
                Could not load employee details
              </Typography>
            </div>
          ) : (
            <>
              {employeeLoading ? (
                <div className={"loading-wrapper"}>
                  <ProgressActivity className={"spinner"} />
                </div>
              ) : (
                <div className={"employee-wrapper"}>
                  <img
                    src={
                      employee?.photo || "https://ionicframework.com/docs/img/demos/thumbnail.svg"
                    }
                    alt={`Employee ${employee?.full_name}`}
                    className={"employee-profile"}
                    onError={e => {
                      e.currentTarget.src =
                        "https://ionicframework.com/docs/img/demos/thumbnail.svg";
                    }}
                  />
                  <Accordion multiple activeIndex={[0]}>
                    <AccordionTab header="Status">
                      <div className={"responsive-grid-2"}>
                        <p>On Site:</p>
                        <p>{statusBodyTemplate(employee?.on_site ? "success" : "fail")}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Compliance:</p>
                        <p>{statusBodyTemplate(employee?.compliance ? "success" : "fail")}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Attendance:</p>
                        <p>
                          {statusBodyTemplate(employee?.attendance_status ? "success" : "fail")}
                        </p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Fatigue management:</p>
                        <p>
                          {statusBodyTemplate(employee?.fatigue_management ? "success" : "fail")}
                        </p>
                      </div>
                    </AccordionTab>
                    <AccordionTab header="Employee details">
                      <div className={"responsive-grid-2"}>
                        <p>Title:</p>
                        <p>{employee?.title}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Name:</p>
                        <p>{employee?.name}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Surname:</p>
                        <p>{employee?.surname}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>ID number:</p>
                        <p>{employee?.id_number}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Email:</p>
                        <p>{employee?.email}</p>
                      </div>
                      <div className={"responsive-grid-2"}>
                        <p>Phone:</p>
                        <p>{employee?.mobile}</p>
                      </div>
                    </AccordionTab>
                    <AccordionTab header={"Site"}>
                      <div className={"responsive-grid-2"}>
                        <p>Appointments:</p>
                        <p>{employee?.appointment_list || "No Appointments"}</p>
                      </div>
                    </AccordionTab>
                    {employee?.on_site && (
                      <AccordionTab header={"Attendance"}>
                        <div className={"responsive-grid-2"}>
                          <p>Last activity:</p>
                          <p>{formatDate(employee?.last_seen)}</p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Current hours:</p>
                          <p>{employee?.attendance_duration || 0}</p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Past seven days:</p>
                          <p>{employee?.past_7_days || 0} hours</p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Past 30 days:</p>
                          <p>{employee?.past_30_days || 0} hours</p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Current month:</p>
                          <p>{employee?.total_hours_current_month || 0} hours</p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Total on site:</p>
                          <p>{employee?.total_hours || 0} hours</p>
                        </div>
                      </AccordionTab>
                    )}
                    {employee?.on_site && (
                      <AccordionTab header={"Fatigue Management"}>
                        <div className={"responsive-grid-2"}>
                          <p>Maximum consecutive working hours:</p>
                          <p>
                            {statusBodyTemplate(
                              employee?.under_consecutive_hours ? "success" : "fail"
                            )}
                          </p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Minimum break duration met:</p>
                          <p>
                            {statusBodyTemplate(
                              employee?.min_break_duration_met ? "success" : "fail"
                            )}
                          </p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Under daily working hours limit:</p>
                          <p>
                            {statusBodyTemplate(
                              employee?.under_daily_hours_limit ? "success" : "fail"
                            )}
                          </p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Daily rest period met:</p>
                          <p>
                            {statusBodyTemplate(
                              employee?.daily_rest_period_met ? "success" : "fail"
                            )}
                          </p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Weekly rest period met:</p>
                          <p>
                            {statusBodyTemplate(
                              employee?.weekly_rest_period_met ? "success" : "fail"
                            )}
                          </p>
                        </div>
                        <div className={"responsive-grid-2"}>
                          <p>Under weekly hours limit:</p>
                          <p>
                            {statusBodyTemplate(
                              employee?.under_weekly_hours_limit ? "success" : "fail"
                            )}
                          </p>
                        </div>
                      </AccordionTab>
                    )}
                  </Accordion>
                </div>
              )}
            </>
          )}
        </TabPanel>
        <TabPanel header={"Employee documents"} key={"employee-documents"}>
          {documentsError ? (
            <div>
              <Typography level="h3" component="h3">
                Could not load employee documents
              </Typography>
            </div>
          ) : (
            <>
              {documentsLoading ? (
                <div className={"loading-wrapper"}>
                  <ProgressActivity className={"spinner"} />
                </div>
              ) : (
                <div className={"document-wrapper"}>
                  <div className={"responsive-grid-3 custom-row-heading"}>
                    <p>Name</p>
                    <p>Description</p>
                  </div>
                  {documents.length === 0 ? (
                    <div className={"responsive-grid-3"}>
                      <p>No documents available</p>
                    </div>
                  ) : (
                    documents.map((document, index) => {
                      return (
                        <div className={"responsive-grid-3"} key={index}>
                          <p>{document.name}</p>
                          <p>
                            {document.description && document.description.length > 0
                              ? document.description
                              : "—"}
                          </p>
                          <p>
                            {document.s3_key ? (
                              <span
                                className={
                                  downloadingArray.includes(document.id)
                                    ? "downloading"
                                    : "download"
                                }
                                onClick={async () => {
                                  if (downloadingArray.includes(document.id)) {
                                    return;
                                  }
                                  await downloadS3File(document);
                                }}
                              >
                                {downloadingArray.includes(document.id) ? (
                                  <ProgressActivity className={"spinner download-icon"} />
                                ) : (
                                  <Download className={"download-icon"} />
                                )}
                              </span>
                            ) : (
                              <span>
                                <DownloadOff className={"nodownload-icon"} />
                              </span>
                            )}
                          </p>
                        </div>
                      );
                    })
                  )}
                </div>
              )}
            </>
          )}
        </TabPanel>
      </TabView>
    </>
  );
}

export default Employee;
